import { LitElement, html, css } from 'lit';
import { customElement } from 'lit/decorators.js';

/**
 * A single slide item for use exclusively as a direct child of `<asc-carousel>`.
 *
 * Accepts arbitrary content via its default slot. All ARIA state
 * (`aria-hidden`, `aria-label`, `aria-roledescription`, `role`) is managed
 * externally by the parent `<asc-carousel>` via `_updateSlideAria()` —
 * `asc-slide` is entirely passive and fires no events.
 *
 * The element occupies exactly one carousel-width in the flex track via
 * `flex: 0 0 100cqi`, where `cqi` resolves from `<asc-carousel>`'s
 * `container-type: inline-size`. Shorter slides vertically center their
 * content within the height of the tallest sibling.
 *
 * @element asc-slide
 *
 * @slot - The slide's content. Accepts any HTML — images, headings, cards, etc.
 *   All slotted content lives in light DOM and is fully indexable by crawlers.
 *
 * @csspart base - Inner `<div>` wrapper; vertically centers slotted content and
 *   applies optional padding and background.
 *
 * @cssprop [--asc-slide-padding] - Uniform inner padding around slotted content.
 * @cssprop [--asc-slide-bg=transparent] - Background color of the content area.
 * @cssprop [--asc-slide-border-radius] - Border radius of the content wrapper.
 */
@customElement('asc-slide')
export class AscSlide extends LitElement {
  static override styles = css`
    :host {
      /*
       * Occupy exactly one carousel-width within the flex track.
       * 100cqi resolves from the nearest container-type: inline-size ancestor,
       * which is <asc-carousel> :host.
       */
      flex: 0 0 100cqi;
      min-width: 0;

      /*
       * Height equalization: the carousel track sets align-items: stretch,
       * so all slides grow to the height of the tallest sibling.
       * display: flex here allows the inner [part="base"] to fill that height.
       */
      display: flex;
      align-items: stretch;
    }

    [part='base'] {
      /* Fill the full equalized height and vertically center content */
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      flex: 1 1 auto;
      min-width: 0;

      padding: var(--asc-slide-padding);
      background: var(--asc-slide-bg, transparent);
      border-radius: var(--asc-slide-border-radius);
    }
  `;

  override render() {
    return html`
      <div part="base">
        <slot></slot>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-slide': AscSlide;
  }
}
